// @mui material components
import Grid from "@mui/material/Grid";
import RefreshIcon from "@mui/icons-material/Refresh";

// formik components
import { FieldArray, useFormikContext } from "formik";
import { useFormAnalyticsContext } from "contexts/contextFormAnalytics";

// Material Dashboard 2 PRO React TS components
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";

import FormField from "components/FormField";

function CumulativesSurvForm({ formData }: any): JSX.Element {
  const { formField, values, errors, touched } = formData;
  const { cumulativesSurvAge1, cumulativesSurvAge2, cumulativesSurvAge3, cumulativesSurvAge4 } =
    formField;

  const { countryDefaults } = useFormAnalyticsContext();
  const { values: v, setValues } = useFormikContext<any>();

  const inc = (2049 - Number(v.startYearInsulin)) / 10;

  const resetValues = () => {
    setValues((prev: any) => ({
      ...prev,
      cumulativesSurvAge1: countryDefaults?.cumulativesSurvAge1 ?? prev.cumulativesSurvAge1,
      cumulativesSurvAge2: countryDefaults?.cumulativesSurvAge2 ?? prev.cumulativesSurvAge2,
      cumulativesSurvAge3: countryDefaults?.cumulativesSurvAge3 ?? prev.cumulativesSurvAge3,
      cumulativesSurvAge4: countryDefaults?.cumulativesSurvAge4 ?? prev.cumulativesSurvAge4,
    }));
  };

  return (
    <MDBox borderRadius="lg" borderColor="red" border={1} pr={1} my={1}>
      <Grid container justifyContent={"space-between"}>
        <Grid item xs={12}>
          <MDTypography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
            Attained cumulatives for{"\n"}relative survival (admin):
          </MDTypography>
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            iconOnly
            onClick={() => resetValues()}
          >
            <RefreshIcon />
          </MDButton>
        </Grid>
        <Grid container xs={12}>
          <Grid item xs={2}></Grid>
          <Grid item xs={2.5}>
            <MDTypography variant="caption">9</MDTypography>
          </Grid>
          <Grid item xs={2.5}>
            <MDTypography variant="caption">28</MDTypography>
          </Grid>
          <Grid item xs={2.5}>
            <MDTypography variant="caption">48</MDTypography>
          </Grid>
          <Grid item xs={2.5}>
            <MDTypography variant="caption">70</MDTypography>
          </Grid>
        </Grid>
        <Grid container xs={2}>
          {Array.from({ length: 10 }, (_, i) =>
            Math.floor(Number(v.startYearInsulin) + inc * (i + 1))
          ).map((year, index) => (
            <Grid item xs={12} key={"cumulativeSurvLabel." + index}>
              <MDTypography variant="caption">{year}</MDTypography>
            </Grid>
          ))}
        </Grid>
        <FieldArray
          name="cumulativesSurvAge1"
          render={(_) => (
            <Grid container xs={2.5}>
              {values.cumulativesSurvAge1.map((cumulative: number, index: number) => (
                <FormField
                  key={`cumulativesSurvAge1.${index}`}
                  type={cumulativesSurvAge1.type}
                  label={cumulativesSurvAge1.label}
                  name={`cumulativesSurvAge1.${index}`}
                  value={cumulative}
                  error={errors.cumulativesSurvAge1 && touched.cumulativesSurvAge1}
                  precision={3}
                />
              ))}
            </Grid>
          )}
        />
        <FieldArray
          name="cumulativesSurvAge2"
          render={(_) => (
            <Grid container xs={2.5}>
              {values.cumulativesSurvAge2.map((cumulative: number, index: number) => (
                <FormField
                  key={`cumulativesSurvAge2.${index}`}
                  type={cumulativesSurvAge2.type}
                  label={cumulativesSurvAge2.label}
                  name={`cumulativesSurvAge2.${index}`}
                  value={cumulative}
                  error={errors.cumulativesSurvAge2 && touched.cumulativesSurvAge2}
                  precision={3}
                />
              ))}
            </Grid>
          )}
        />
        <FieldArray
          name="cumulativesSurvAge3"
          render={(_) => (
            <Grid container xs={2.5}>
              {values.cumulativesSurvAge3.map((cumulative: number, index: number) => (
                <FormField
                  key={`cumulativesSurvAge3.${index}`}
                  type={cumulativesSurvAge3.type}
                  label={cumulativesSurvAge3.label}
                  name={`cumulativesSurvAge3.${index}`}
                  value={cumulative}
                  error={errors.cumulativesSurvAge3 && touched.cumulativesSurvAge3}
                  precision={3}
                />
              ))}
            </Grid>
          )}
        />
        <FieldArray
          name="cumulativesSurvAge4"
          render={(_) => (
            <Grid container xs={2.5}>
              {values.cumulativesSurvAge4.map((cumulative: number, index: number) => (
                <FormField
                  key={`cumulativesSurvAge4.${index}`}
                  type={cumulativesSurvAge4.type}
                  label={cumulativesSurvAge4.label}
                  name={`cumulativesSurvAge4.${index}`}
                  value={cumulative}
                  placeholder={cumulativesSurvAge4.placeholder}
                  error={errors.cumulativesSurvAge4 && touched.cumulativesSurvAge4}
                  precision={3}
                />
              ))}
            </Grid>
          )}
        />
        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="error"
            fontWeight="regular"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {errors.cumulativesSurvAge1 ||
              errors.cumulativesSurvAge2 ||
              errors.cumulativesSurvAge3 ||
              errors.cumulativesSurvAge4}
          </MDTypography>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default CumulativesSurvForm;
